import type { KkmServerResponse } from '@/shared/api';

import { isRecord, optionalString } from '@/shared/api';

import { extractKkmFiscalDocumentDraft } from './kkm-fiscal-document-parser';

/**
 * Результат проверки одного кода маркировки в ответе `RegisterCheck` или
 * `ValidationMarkingCode`.
 *
 * Формы `MarkingCodeValidation` нет в сэмплах 2.3.16.40 — она известна только по
 * live-примеру с форума (2.3.16.15, АТОЛ, ФФД 1.2) и подтверждается стендовой
 * приёмкой. На живом ответе ТС ПИоТ `ValidationPR.ValidationResult` приходит
 * булевым, а `ValidationKKT.ValidationResult` — числом тега 2106, поэтому обе
 * формы нормализуются в `boolean`. См. подраздел «Не подтверждено сэмплами
 * 2.3.16.40» в `.ai-factory/references/kkmserver-api-commands.md`.
 */
export interface KkmMarkingValidation {
  barCode: string | null;
  /** Расшифровка кода: показывается кассиру, чтобы он понял, какую позицию убрать. */
  decryptionResult: string | null;
  /** Проверка ККТ прошла; `null` — поля в ответе не было. */
  kktPassed: boolean | null;
  /** Проверка в ТС ПИоТ (разрешительный режим) прошла; `null` — поля не было. */
  prPassed: boolean | null;
  /**
   * Код принадлежит нам по данным ГИС МТ; `null` — признака в ответе не было.
   *
   * `false` означает, что УПД на партию ещё не подписан: оплату это не
   * блокирует, но чек в ГИС МТ получит статус «обработан с ошибкой».
   */
  isOwner: boolean | null;
}

/** Бит «проверка выполнена и код достоверен» тега 2106. */
const VALIDATION_PASSED = 15;

export function parseMarkingValidation(raw: Readonly<Record<string, unknown>>): KkmMarkingValidation[] {
  const items = raw.MarkingCodeValidation;

  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(isRecord).map((item) => ({
    barCode: optionalString(item.BarCode),
    decryptionResult: optionalString(item.DecryptionResult),
    kktPassed: validationPassed(item.ValidationKKT),
    prPassed: validationPassed(item.ValidationPR),
    isOwner: ownershipFlag(item.ValidationPR),
  }));
}

/** Коды, которые ГИС МТ не считает нашими: партия без подписанного УПД. */
export function unownedMarkingCodes(response: KkmServerResponse): string[] {
  return parseMarkingValidation(response.raw)
    .filter((validation) => validation.isOwner === false)
    .map((validation) => validation.barCode)
    .filter((code): code is string => code !== null);
}

/**
 * Ответ ККТ — отказ именно по коду маркировки, а не что-то ещё.
 *
 * Эвристика намеренно узкая: `Status: 2` без массива проверок остаётся
 * неизвестностью, потому что тем же статусом расширение отдаёт собственные
 * ошибки («Расширение … не настроенно!»). Текстовых эвристик по `Error` нет:
 * ошибочно признать отказ доказанным — значит разрешить повтор необратимой
 * операции. Реквизиты фискального документа в ответе означают, что чек всё же
 * напечатан, и никакой отказ уже не «до печати».
 */
export function isMarkingRefusal(response: KkmServerResponse): boolean {
  if (response.status !== 2) {
    return false;
  }

  const validations = parseMarkingValidation(response.raw);

  if (validations.length === 0 || !validations.some(isFailed)) {
    return false;
  }

  const draft = extractKkmFiscalDocumentDraft(response);

  return draft.fiscalDocumentNumber === null && draft.fiscalDocumentSign === null && draft.fiscalDriveNumber === null;
}

/** Код, из-за которого чек не напечатан: хвост для сообщения кассиру. */
export function failedMarkingCode(response: KkmServerResponse): string | null {
  const failed = parseMarkingValidation(response.raw).find(isFailed);

  return failed?.decryptionResult ?? failed?.barCode ?? null;
}

const isFailed = (validation: KkmMarkingValidation): boolean =>
  validation.prPassed === false || validation.kktPassed === false;

/** Число — тег 2106 (`15` = пройдено), булево — живой ответ ТС ПИоТ. */
const validationPassed = (value: unknown): boolean | null => {
  if (!isRecord(value)) {
    return null;
  }

  const result = value.ValidationResult;

  if (typeof result === 'boolean') {
    return result;
  }

  return typeof result === 'number' ? result === VALIDATION_PASSED : null;
};

/**
 * Признак владения приходит либо отдельным полем `ValidationPR.isOwner`, либо
 * внутри `Log` — сырого ответа ГИС МТ, который KKMServer отдаёт объектом или
 * JSON-строкой.
 */
const ownershipFlag = (value: unknown): boolean | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.isOwner === 'boolean') {
    return value.isOwner;
  }

  const log = parseLog(value.Log);

  if (log === null) {
    return null;
  }

  if (typeof log.isOwner === 'boolean') {
    return log.isOwner;
  }

  const first = Array.isArray(log.codes) ? log.codes[0] : null;

  return isRecord(first) && typeof first.isOwner === 'boolean' ? first.isOwner : null;
};

const parseLog = (value: unknown): Record<string, unknown> | null => {
  if (isRecord(value)) {
    return value;
  }

  if (typeof value !== 'string' || value === '') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);

    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
